import { useCallback, useRef, useState } from "react";
import { SSE } from "sse.js";

import { AiChatRequest } from "../../../services/aiChat";
import { determineBaseUrl } from "../../../services/emptyApi";
import { getAccessTokenSilently } from "../../../shared/Auth0Utility";
import {
  ContentBlockDeltaEvent,
  ContentBlockStartEvent,
  ContentBlockStopEvent,
  MessageStartEvent,
  MessageStopEvent,
  StreamChatState,
} from "../../../types/aiChatStream";

interface UseStreamChatOptions {
  onStart?: (messageId: string, model: string) => void;
  onContent?: (
    content: string,
    fullContent: string,
    textContent: string,
  ) => void;
  onComplete?: (
    content: string,
    stopReason: string,
    usage: { input_tokens: number; output_tokens: number },
    containerId?: string | null,
    textContent?: string,
  ) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for streaming AI chat responses via SSE
 *
 * @example
 * ```tsx
 * const { streamChat, streamedContent, isStreaming, abort } = useStreamChat({
 *   onComplete: (content) => {
 *     // Add assistant message to chat history
 *     setChatMessages(prev => [...prev, { role: 'assistant', content }]);
 *   },
 * });
 *
 * // Start streaming
 * streamChat({ session_id: 'abc', messages: [...] });
 *
 * // Abort if needed
 * abort();
 * ```
 */
export const useStreamChat = (options: UseStreamChatOptions = {}) => {
  const { onStart, onContent, onComplete, onError } = options;

  const [state, setState] = useState<StreamChatState>({
    isStreaming: false,
    streamedContent: "",
    messageId: null,
    model: null,
    error: null,
    usage: null,
    stopReason: null,
  });

  // Track the SSE source for abort functionality
  const sseSourceRef = useRef<SSE | null>(null);
  // Track accumulated content
  const contentRef = useRef<string>("");
  // Accumulates only chunks whose content block is of type "text" (skips
  // tool_use JSON / tool-result blobs). Used by callers like useInitContainer
  // to get Claude's human-readable summary without the bash transcript.
  const textContentRef = useRef<string>("");
  // Current block's type, set from content_block_start. Resets on stop.
  const currentBlockTypeRef = useRef<string | null>(null);

  const abort = useCallback(() => {
    if (sseSourceRef.current) {
      sseSourceRef.current.close();
      sseSourceRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      isStreaming: false,
    }));
  }, []);

  const streamChat = useCallback(
    async (request: AiChatRequest) => {
      // Reset state for new stream
      contentRef.current = "";
      textContentRef.current = "";
      currentBlockTypeRef.current = null;
      setState({
        isStreaming: true,
        streamedContent: "",
        messageId: null,
        model: null,
        error: null,
        usage: null,
        stopReason: null,
      });

      try {
        const authToken = await getAccessTokenSilently();
        const baseUrl = determineBaseUrl();

        const source = new SSE(`${baseUrl}ai/chat`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          payload: JSON.stringify(request),
          method: "POST",
        });

        sseSourceRef.current = source;

        // Handle message_start event
        source.addEventListener("message_start", ((event: MessageEvent) => {
          try {
            const data: MessageStartEvent = JSON.parse(event.data);
            setState((prev) => ({
              ...prev,
              messageId: data.message_id,
              model: data.model,
            }));
            onStart?.(data.message_id, data.model);
          } catch (e) {
            console.error("Failed to parse message_start event:", e);
          }
        }) as EventListener);

        // Track current block's type so we can isolate text-only output.
        source.addEventListener("content_block_start", ((
          event: MessageEvent,
        ) => {
          try {
            const data: ContentBlockStartEvent = JSON.parse(event.data);
            currentBlockTypeRef.current = data.content_block_type ?? null;
          } catch (e) {
            console.error("Failed to parse content_block_start event:", e);
          }
        }) as EventListener);

        source.addEventListener("content_block_stop", ((
          event: MessageEvent,
        ) => {
          try {
            const data: ContentBlockStopEvent = JSON.parse(event.data);
            void data;
            currentBlockTypeRef.current = null;
          } catch (e) {
            console.error("Failed to parse content_block_stop event:", e);
          }
        }) as EventListener);

        // Handle content_block_delta event
        source.addEventListener("content_block_delta", ((
          event: MessageEvent,
        ) => {
          try {
            const data: ContentBlockDeltaEvent = JSON.parse(event.data);
            let chunk = data.content;
            const current = contentRef.current;
            // Ensure space between sentences when stream sends chunks split at period (e.g. "with." then "Let" -> "with. Let")
            if (
              current.length > 0 &&
              /[.!?]$/.test(current) &&
              chunk.length > 0 &&
              /^[A-Za-z]/.test(chunk) &&
              chunk[0] !== " "
            ) {
              chunk = ` ${chunk}`;
            }
            contentRef.current += chunk;
            if (currentBlockTypeRef.current === "text") {
              textContentRef.current += chunk;
            }
            const fullContent = contentRef.current;

            setState((prev) => ({
              ...prev,
              streamedContent: fullContent,
            }));
            onContent?.(data.content, fullContent, textContentRef.current);
          } catch (e) {
            console.error("Failed to parse content_block_delta event:", e);
          }
        }) as EventListener);

        // Handle message_stop event
        source.addEventListener("message_stop", ((event: MessageEvent) => {
          try {
            const data: MessageStopEvent = JSON.parse(event.data);
            const finalContent = contentRef.current;

            setState((prev) => ({
              ...prev,
              isStreaming: false,
              usage: data.usage,
              stopReason: data.stop_reason,
            }));

            onComplete?.(
              finalContent,
              data.stop_reason,
              data.usage,
              data.container_id,
              textContentRef.current,
            );

            // Clean up
            source.close();
            sseSourceRef.current = null;
          } catch (e) {
            console.error("Failed to parse message_stop event:", e);
          }
        }) as EventListener);

        // Handle API error events
        source.addEventListener("error", ((event: MessageEvent) => {
          // Ignore XHR errors that fire after stream completed successfully
          if (!sseSourceRef.current) {
            return;
          }

          let errorMessage = "Stream connection failed";

          // Try to parse the error data from the SSE event
          if (event.data) {
            try {
              const data = JSON.parse(event.data);
              if (data.error) {
                errorMessage = data.error;
              } else if (data.message) {
                errorMessage = data.message;
              }
            } catch {
              // If parsing fails, use the raw data as the error message
              if (typeof event.data === "string" && event.data.length > 0) {
                errorMessage = event.data;
              }
            }
          }

          const error = new Error(errorMessage);
          console.error("SSE stream error:", errorMessage, event);

          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error,
          }));
          onError?.(error);

          source.close();
          sseSourceRef.current = null;
        }) as EventListener);

        // Start the stream
        source.stream();
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: err,
        }));
        onError?.(err);
      }
    },
    [onStart, onContent, onComplete, onError],
  );

  return {
    streamChat,
    abort,
    ...state,
  };
};
